<script setup>
	import { reactive } from "vue";
	import { executeServiceCall, getValidationErrorsFromResponse, selfServiceService } from "@/services";

	const mainData = reactive({
		contact: {
			emailAddress: "",
			name: "",
			phoneNumber: "",
			recaptchaResponse: "skip",
			text: "",
		},
		hasCriticalError: false,
		isProcessing: false,
		validationErrors: [],
		submitted: false,
	});

	async function submit() {
		const clientErrors = [];
		if (!mainData.contact.name.trim()) clientErrors.push("Your name is required.");
		if (!mainData.contact.emailAddress.trim()) clientErrors.push("Your e-mail address is required.");
		if (!mainData.contact.text.trim()) clientErrors.push("Please let us know what you'd like to discuss.");

		if (clientErrors.length > 0) {
			mainData.validationErrors = clientErrors;
			mainData.hasCriticalError = false;
			return;
		}

		mainData.isProcessing = true;
		mainData.hasCriticalError = false;
		mainData.validationErrors = [];

		await executeServiceCall(() => selfServiceService.public.submitContactRequest(mainData.contact))
			.then(() => {
				mainData.submitted = true;
				mainData.contact = {
					emailAddress: "",
					name: "",
					phoneNumber: "",
					recaptchaResponse: "skip",
					text: "",
				};
			})
			.catch(error => {
				if (error?.response?.status === 400) {
					mainData.validationErrors = getValidationErrorsFromResponse(error.response);
				} else {
					console.error(error);
					mainData.hasCriticalError = true;
				}
			})
			.finally(() => (mainData.isProcessing = false));
	}
</script>

<template>
	<div class="contact-page">
		<section class="hero">
			<div class="hero-inner">
				<span class="block-eyebrow">Contact</span>
				<h1 class="hero-title">Let's talk about your project</h1>
				<p class="hero-lead">
					Want a quick estimate or a second opinion? Call us at
					<a href="tel:817-476-2110" class="hero-phone">817-476-2110</a>
					or send a message below.
				</p>
			</div>
		</section>

		<section class="contact-section">
			<div class="contact-grid">
				<aside class="info-card">
					<span class="info-eyebrow">Get in touch</span>
					<h2 class="info-heading">We're here to help</h2>

					<a href="tel:817-476-2110" class="info-phone">817-476-2110</a>
					<p class="info-email-line">
						<span class="info-email-icon" aria-hidden="true">
							<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 7l9 6 9-6" /></svg>
						</span>
						Prefer email? Use the form →
					</p>

					<p class="info-description">
						Tell us a bit about your goals. A senior engineer will follow up with next
						steps — no pressure, no jargon.
					</p>
				</aside>

				<div class="form-card">
					<template v-if="!mainData.submitted">
						<h2 class="form-heading">Send us a message</h2>

						<div v-if="mainData.validationErrors.length > 0" class="feedback feedback-error" role="alert">
							<p class="feedback-title">Please fix the following:</p>
							<ul class="feedback-list">
								<li v-for="(err, i) in mainData.validationErrors" :key="i">{{ err }}</li>
							</ul>
						</div>
						<p v-else-if="mainData.hasCriticalError" class="feedback feedback-error" role="alert">Something went wrong sending your message. Please try again.</p>

						<div class="field">
							<label for="contactName">Your name</label>
							<input id="contactName" v-model="mainData.contact.name" type="text" autocomplete="name" :disabled="mainData.isProcessing" />
						</div>
						<div class="field">
							<label for="contactEmailAddress">Your e-mail address</label>
							<input id="contactEmailAddress" v-model="mainData.contact.emailAddress" type="email" autocomplete="email" :disabled="mainData.isProcessing" />
						</div>
						<div class="field">
							<label for="contactPhoneNumber">Your phone number</label>
							<input id="contactPhoneNumber" v-model="mainData.contact.phoneNumber" type="tel" autocomplete="tel" :disabled="mainData.isProcessing" />
							<p class="field-help">No text messages will be sent to this number.</p>
						</div>
						<div class="field">
							<label for="contactText">What would you like to know?</label>
							<textarea id="contactText" v-model="mainData.contact.text" rows="5" :disabled="mainData.isProcessing"></textarea>
						</div>

						<button
							type="button"
							class="btn btn-primary btn-block submit-contact-request-action"
							:disabled="mainData.isProcessing"
							@click="submit"
						>
							<span v-if="mainData.isProcessing" class="btn-spinner" aria-hidden="true"></span>{{ mainData.isProcessing ? "Sending…" : "Submit" }}
						</button>
					</template>

					<div v-else class="success">
						<span class="success-check" aria-hidden="true">
							<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M5 13l4 4L19 7" /></svg>
						</span>
						<h2 class="success-title">Thanks — we've received your message.</h2>
						<p class="success-text">We'll get back to you as soon as possible, usually within one business day.</p>
					</div>
				</div>
			</div>
		</section>
	</div>
</template>

<style scoped>
	.contact-page {
		--bg: #f5f9fd;
		--surface: #ffffff;
		--surface-muted: #eef4fb;
		--border: #e2ecf6;
		--border-strong: #cddcec;
		--navy: #0f2742;
		--ink: #16314f;
		--ink-soft: #4a5d75;
		--ink-muted: #7589a1;
		--sky: #0ea5e9;
		--sky-deep: #0284c7;
		--blue: #2563eb;
		--blue-deep: #1d4ed8;
		--blue-soft: #dceafe;

		font-family: "Poppins", "Plus Jakarta Sans", "Segoe UI", -apple-system, BlinkMacSystemFont, sans-serif;
		color: var(--ink);
		background: var(--bg);
		overflow-x: hidden;
	}

	.btn {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		padding: 0.95rem 1.9rem;
		border-radius: 12px;
		font-family: inherit;
		font-size: 1.05rem;
		font-weight: 700;
		text-decoration: none;
		cursor: pointer;
		border: 1px solid transparent;
		transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease, border-color 0.2s ease, color 0.2s ease;
	}

	.btn-primary {
		background: var(--blue);
		color: #ffffff;
		box-shadow: 0 14px 28px -14px rgba(37, 99, 235, 0.7);
	}

	.btn-primary:hover {
		transform: translateY(-2px);
		background: var(--blue-deep);
		box-shadow: 0 20px 34px -16px rgba(37, 99, 235, 0.75);
	}

	.btn-block {
		width: 100%;
	}

	.btn:disabled {
		opacity: 0.65;
		cursor: not-allowed;
		transform: none;
		box-shadow: none;
	}

	.btn-spinner {
		width: 1.05em;
		height: 1.05em;
		flex-shrink: 0;
		margin-right: 0.55rem;
		border: 2px solid rgba(255, 255, 255, 0.45);
		border-top-color: #ffffff;
		border-radius: 50%;
		-webkit-animation: btn-spin 0.7s linear infinite;
		animation: btn-spin 0.7s linear infinite;
	}

	@-webkit-keyframes btn-spin {
		to { -webkit-transform: rotate(360deg); }
	}

	@keyframes btn-spin {
		to { transform: rotate(360deg); }
	}

	.hero {
		padding: clamp(3.5rem, 8vw, 6rem) 1.5rem clamp(2rem, 4vw, 3rem);
	}

	.hero-inner {
		max-width: 820px;
		margin: 0 auto;
		text-align: center;
	}

	.block-eyebrow {
		display: inline-block;
		font-size: 0.85rem;
		font-weight: 700;
		letter-spacing: 0.12em;
		text-transform: uppercase;
		color: var(--blue-deep);
		margin-bottom: 0.7rem;
	}

	.hero-title {
		font-size: clamp(2.2rem, 4.2vw, 3.4rem);
		line-height: 1.08;
		font-weight: 800;
		letter-spacing: -0.025em;
		color: var(--navy);
		margin: 0 0 1rem;
	}

	.hero-lead {
		font-size: clamp(1.08rem, 1.4vw, 1.28rem);
		line-height: 1.65;
		color: var(--ink-soft);
		max-width: 52ch;
		margin: 0 auto;
	}

	.hero-phone {
		color: var(--blue-deep);
		text-decoration: none;
		font-weight: 700;
		white-space: nowrap;
	}

	.hero-phone:hover {
		text-decoration: underline;
	}

	.contact-section {
		padding: clamp(1rem, 3vw, 2rem) 1.5rem clamp(3.5rem, 7vw, 6rem);
	}

	.contact-grid {
		max-width: 1140px;
		margin: 0 auto;
		display: grid;
		grid-template-columns: 0.85fr 1.15fr;
		gap: 1.5rem;
		align-items: stretch;
	}

	.info-card {
		padding: clamp(1.85rem, 3vw, 2.5rem);
		border-radius: 20px;
		background: var(--surface);
		border: 1px solid var(--border);
		box-shadow: 0 30px 60px -44px rgba(15, 39, 66, 0.45);
	}

	.info-eyebrow {
		display: inline-block;
		font-size: 0.8rem;
		font-weight: 700;
		letter-spacing: 0.12em;
		text-transform: uppercase;
		color: var(--blue-deep);
		margin-bottom: 0.6rem;
	}

	.info-heading {
		font-size: clamp(1.4rem, 2vw, 1.8rem);
		font-weight: 800;
		letter-spacing: -0.02em;
		color: var(--navy);
		margin: 0 0 1.5rem;
	}

	.info-phone {
		display: inline-block;
		font-size: clamp(1.6rem, 2.4vw, 2.1rem);
		font-weight: 800;
		letter-spacing: -0.02em;
		color: var(--blue-deep);
		text-decoration: none;
	}

	.info-phone:hover {
		text-decoration: underline;
	}

	.info-email-line {
		display: flex;
		align-items: center;
		gap: 0.55rem;
		margin: 0.85rem 0 0;
		color: var(--ink-soft);
		font-size: 1rem;
	}

	.info-email-icon {
		display: inline-flex;
		color: var(--blue);
	}

	.info-email-icon svg {
		width: 20px;
		height: 20px;
	}

	.info-description {
		margin: 1.5rem 0 0;
		font-size: 1.02rem;
		line-height: 1.65;
		color: var(--ink-soft);
	}

	.form-card {
		padding: clamp(1.85rem, 3vw, 2.75rem);
		border-radius: 20px;
		background: var(--surface);
		border: 1px solid var(--border);
		box-shadow: 0 34px 64px -42px rgba(15, 39, 66, 0.45);
	}

	.form-heading {
		font-size: clamp(1.35rem, 2vw, 1.7rem);
		font-weight: 800;
		letter-spacing: -0.02em;
		color: var(--navy);
		margin: 0 0 1.5rem;
	}

	.field {
		margin-bottom: 1.15rem;
	}

	.field label {
		display: block;
		font-size: 0.95rem;
		font-weight: 600;
		color: var(--ink);
		margin-bottom: 0.45rem;
	}

	.field input,
	.field textarea {
		width: 100%;
		padding: 0.85rem 1rem;
		border-radius: 10px;
		border: 1px solid var(--border-strong);
		background: var(--bg);
		font-family: inherit;
		font-size: 1rem;
		color: var(--ink);
		transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
		box-sizing: border-box;
	}

	.field textarea {
		resize: vertical;
		min-height: 140px;
	}

	.field input:focus,
	.field textarea:focus {
		outline: none;
		background: var(--surface);
		border-color: var(--blue);
		box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.18);
	}

	.field input:disabled,
	.field textarea:disabled {
		opacity: 0.6;
		cursor: not-allowed;
	}

	.field-help {
		margin: 0.4rem 0 0;
		font-size: 0.85rem;
		color: var(--ink-muted);
	}

	.feedback {
		margin: 0 0 1.3rem;
		padding: 0.85rem 1rem;
		border-radius: 12px;
		font-size: 0.97rem;
		font-weight: 500;
	}

	.feedback-error {
		background: #fbe9ec;
		color: #9c1f2e;
	}

	.feedback-title {
		margin: 0 0 0.4rem;
		font-weight: 700;
	}

	.feedback-list {
		margin: 0;
		padding-left: 1.2rem;
	}

	.feedback-list li {
		margin-bottom: 0.2rem;
	}

	.success {
		text-align: center;
		padding: 1.75rem 0.5rem;
	}

	.success-check {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 72px;
		height: 72px;
		border-radius: 50%;
		background: #dcfce7;
		color: #16a34a;
		margin-bottom: 1.35rem;
	}

	.success-check svg {
		width: 36px;
		height: 36px;
	}

	.success-title {
		font-size: clamp(1.3rem, 2vw, 1.6rem);
		font-weight: 800;
		color: var(--navy);
		margin: 0 0 0.6rem;
	}

	.success-text {
		color: var(--ink-soft);
		line-height: 1.6;
		margin: 0;
	}

	@media (max-width: 900px) {
		.contact-grid { grid-template-columns: 1fr; }
	}
</style>